import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Pagination from '@mui/material/Pagination';
import { visuallyHidden } from '@mui/utils';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
};

const getComparator = (order, orderBy) =>
  order === 'asc'
    ? (a, b) => -descendingComparator(a, b, orderBy)
    : (a, b) => descendingComparator(a, b, orderBy);

const DataTable = ({
  columns,
  data,
  total,
  title,
  onSelectionChange,
  onRowClick,
  rowsPerPage,
  onPageChange,
  currentPage,
  selectedRows = [], // External row selection support
  initialOrder = 'desc', // New prop to allow setting initial sort order (default to 'desc' for latest dates first)
  initialOrderBy, // New prop to set initial sort column
}) => {
  const [order, setOrder] = React.useState(initialOrder || 'asc');
  const [orderBy, setOrderBy] = React.useState(initialOrderBy || columns[0]?.id || '');
  const [selected, setSelected] = React.useState([]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = data.map((n) => n.id);
      setSelected(newSelected);
      onSelectionChange?.(newSelected);
    } else {
      setSelected([]);
      onSelectionChange?.([]);
    }
  };

  const visibleRows = React.useMemo(
    () => [...data].sort(getComparator(order, orderBy)),
    [order, orderBy, data]
  );

  const handleRowClick = (rowData) => {
    onRowClick?.(rowData);
  };

  const totalPages = Math.ceil(total / rowsPerPage);

  const handlePageChange = (event, page) => {
    onPageChange?.(page);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? 'right' : 'left'}
                    padding={column.disablePadding ? 'none' : 'normal'}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    {column.id === 'checkbox' ? (
                      <Checkbox
                        color="primary"
                        indeterminate={
                          selected.length > 0 && selected.length < data.length
                        }
                        checked={
                          data.length > 0 && selected.length === data.length
                        }
                        onChange={handleSelectAllClick}
                        inputProps={{ 'aria-label': 'select all rows' }}
                      />
                    ) : (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, column.id)}
                      >
                        {column.label}
                        {orderBy === column.id && (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        )}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => {
                // Use external selectedRows if provided, otherwise use internal selected state
                const rowId = row.id || row.GPOfferID; // Support both GPOfferID and id
                const isItemSelected = selectedRows.length > 0 
                  ? selectedRows.includes(rowId)
                  : selected.includes(rowId);

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={rowId}
                    selected={isItemSelected}
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5 !important',
                        transition: 'background-color 0.2s ease-in-out',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#fff4cd !important', // Use the same color as the CSS
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: '#ffeb9c !important', // Slightly darker when hovered and selected
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={`${rowId}-${column.id}`}
                        align={column.numeric ? 'right' : 'left'}
                        padding={column.disablePadding ? 'none' : 'normal'}
                      >
                        {column.renderCell
                          ? column.renderCell(row)
                          : row[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {total !== undefined && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              // showFirstButton
              // showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  backgroundColor: '#f0f0f0',
                  '&.Mui-selected': {
                    backgroundColor: '#de6b14',
                    color: '#fff',
                  },
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                    color: '#de6b14',
                  },
                },
              }}
            />
          </Box>
        )}
      

</Paper>
    </Box>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      numeric: PropTypes.bool,
      label: PropTypes.string.isRequired,
      disablePadding: PropTypes.bool,
      renderCell: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  onSelectionChange: PropTypes.func,
  onRowClick: PropTypes.func,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func,
  currentPage: PropTypes.number.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  initialOrder: PropTypes.oneOf(['asc', 'desc']),
  initialOrderBy: PropTypes.string,
};

export default DataTable;